import { gql } from 'graphql-tag';
import ButtonGroup from '@mui/material/ButtonGroup';

import { getCurrentDevEnv } from '@watershed/shared-frontend/utils/devEnv';
import { getGqlResultDataBang } from '@watershed/shared-frontend/utils/errorUtils';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { switchPinnedOrganizationId } from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import { loginAsApiEndpointUrl } from '@watershed/shared-universal/adminRoutes';
import { userCanAccessOrganization } from '@watershed/shared-universal/entFound/accessUtils';
import {
  PreviewDeployName,
  previewDeployNames,
} from '@watershed/shared-universal/utils/helpers';
import must from '@watershed/shared-universal/utils/must';
import * as Sentry from '@watershed/shared-universal/utils/sentry';
import Button, { ButtonProps } from '@watershed/ui-core/components/Button';
import DropdownMenu from '@watershed/ui-core/components/DropdownMenu';

import {
  useCanAccessOrganizationQuery,
  useLoginAsUserMutation,
} from '@watershed/app-admin/generated/urql';

import { useAdminContext } from './AdminContext';

gql`
  mutation LoginAsUser($input: LoginAsUserInput!) {
    loginAsUser(input: $input) {
      token
      isSuccessful
    }
  }
`;

export function LoginAsButtons({
  targetOrgId,
  userId,
  redirect,
  size = 'small',
  hidePreviewDeployOptions = false,
  ...props
}: {
  targetOrgId: string;
  userId?: string;
  redirect?: string;
  hidePreviewDeployOptions?: boolean;
  size?: ButtonProps['size'];
} & ButtonProps) {
  const { activeWatershedEmployee } = useAdminContext();
  const [result] = useCanAccessOrganizationQuery({
    variables: { orgId: targetOrgId },
  });

  if (isFetchingOrStale(result)) {
    return <div>Loading...</div>;
  }

  const { canAccessOrganization, organization } = getGqlResultDataBang(result);

  // If we don't have access to the org or the org is locked, disable the login as button
  const loginAsOtherDisabled =
    !userCanAccessOrganization({
      canAccessOrganization,
      org: organization,
    }) || organization.lockedAt != null;

  const loginDisabledExplanation =
    organization.lockedAt != null
      ? `This organization is locked for ${organization.lockedReason}`
      : 'You do not have access to this customer organization';

  const accessibleOrgIds = activeWatershedEmployee.user.accessibleOrgs.map(
    (org) => org.id
  );
  const targetLoginUserIsMyself = activeWatershedEmployee.user.id === userId;

  // if i have direct access to the org, and the target login as user is myself or unset
  // show the login as myself button instead of login as button
  const shouldShowLoginAsMyself =
    (targetLoginUserIsMyself || !userId) &&
    accessibleOrgIds.includes(targetOrgId);

  return shouldShowLoginAsMyself ? (
    <LoginAsMyselfButton
      targetOrgId={targetOrgId}
      wsEmployeeUserName={activeWatershedEmployee.name}
      hidePreviewDeployOptions={hidePreviewDeployOptions}
      size={size}
      redirect={redirect}
      {...props}
    >
      {props.children}
    </LoginAsMyselfButton>
  ) : (
    <LoginAsButton
      {...props}
      targetOrgId={targetOrgId}
      hidePreviewDeployOptions={hidePreviewDeployOptions}
      userId={userId}
      redirect={redirect}
      size={size}
      disabled={props.disabled || loginAsOtherDisabled}
      tooltip={loginAsOtherDisabled ? loginDisabledExplanation : undefined}
    >
      {props.children}
    </LoginAsButton>
  );
}

const LoginAsButtonBase: React.FC<
  {
    onClickLogin: (previewDeployName?: PreviewDeployName) => void;
    children: React.ReactNode;
    hidePreviewDeployOptions: boolean;
  } & ButtonProps
> = ({ onClickLogin, children, hidePreviewDeployOptions, ...props }) => {
  const shouldRenderPreviewDeployOptions =
    getCurrentDevEnv() !== 'local-dev' && !hidePreviewDeployOptions;
  return (
    <ButtonGroup size={props.size}>
      <Button
        {...props}
        onClick={(e) => {
          // to stop opening the org page when clicking the button
          e.stopPropagation();
          onClickLogin(undefined);
        }}
      >
        {children}
      </Button>
      {shouldRenderPreviewDeployOptions && (
        <DropdownMenu
          buttonProps={props}
          items={[
            {
              id: 'default',
              label: 'Default',
              // Explicitly set no preview deploy
              onSelect: () => onClickLogin(undefined),
            },
            ...previewDeployNames.map((name) => ({
              id: name,
              label: name,
              onSelect: () => onClickLogin(name),
            })),
          ]}
        />
      )}
    </ButtonGroup>
  );
};

function LoginAsButton({
  targetOrgId,
  hidePreviewDeployOptions,
  userId,
  redirect,
  size = 'small',
  disabled,
  tooltip,
  ...props
}: {
  targetOrgId: string;
  hidePreviewDeployOptions: boolean;
  userId?: string;
  redirect?: string;
  size?: ButtonProps['size'];
  disabled?: boolean;
  tooltip?: string;
} & ButtonProps) {
  const [, loginAsUser] = useLoginAsUserMutation();
  const handleClick = async (previewDeployName?: PreviewDeployName) => {
    const result = await loginAsUser({
      input: { targetOrgId, targetUserId: userId },
    });
    if (result.error || !result.data) {
      alert('Error logging in as');
    }
    const data = must(result.data);
    const token = data.loginAsUser.token;
    if (data.loginAsUser.token) {
      const url = loginAsApiEndpointUrl({
        orgId: targetOrgId,
        userId,
        redirect,
        token: token ?? undefined,
        previewDeployName,
      });
      window.open(url, '_blank', 'noopener');
    } else {
      Sentry.captureException(
        new Error(
          "Can't generate login as token (DRI: @enterprise-foundations-oncall)"
        ),
        { level: 'warning' }
      );
      alert(
        'Error generating login as token. Something is broken, ask in #errors'
      );
    }
  };

  return (
    <LoginAsButtonBase
      onClickLogin={handleClick}
      size={size}
      hidePreviewDeployOptions={hidePreviewDeployOptions}
      disabled={disabled}
      tooltip={tooltip}
    >
      [As Other] {props.children}
    </LoginAsButtonBase>
  );
}

export function LoginAsMyselfButton({
  targetOrgId,
  redirect,
  wsEmployeeUserName,
  hidePreviewDeployOptions,
  ...props
}: {
  targetOrgId: string;
  wsEmployeeUserName: string;
  hidePreviewDeployOptions: boolean;
  redirect?: string;
  userId?: string;
} & ButtonProps) {
  const handleClick = (previewDeployName?: PreviewDeployName) => {
    switchPinnedOrganizationId({
      orgId: targetOrgId,
      redirect,
      reloadInOrgHomeIfLoggedOut: true,
      previewDeployName,
    });
  };

  return (
    <LoginAsButtonBase
      {...props}
      onClickLogin={handleClick}
      hidePreviewDeployOptions={hidePreviewDeployOptions}
    >
      {props.children} as {wsEmployeeUserName}
    </LoginAsButtonBase>
  );
}
